import { css } from '@emotion/react';
import { useState } from 'react';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import {
  primary, white, gray1, gray3, gray5, gray7, warning, hexToRgbaString, black,
} from 'styles/global_defaults/colors';
import { halfSpacing, largeSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';

export type FlipCardData = {
  id: number,
  front: string,
  back: string,
};

const CARD_HEIGHT = 280;
const CARD_RADIUS = 10;

const FlipCard = ({ card }: { card: FlipCardData }) => {
  const [flipped, setFlipped] = useState(false);

  const styles = css`
    position: relative;
    width: 100%;
    height: ${CARD_HEIGHT}px;
    perspective: 1400px;

    .flip-indicator {
      position: absolute;
      bottom: ${halfSpacing}px;
      right: ${halfSpacing}px;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${standardSpacing}px;
      height: ${standardSpacing}px;
      border-radius: 50%;
      color: ${gray3};
      background: ${white};
      box-shadow: 0 2px 8px ${hexToRgbaString(black, 0.12)};

      &::before {
        font-size: 10px;
      }
    }

    .flip-card-inner {
      position: relative;
      width: 100%;
      height: 100%;
      transition: transform 0.6s cubic-bezier(0.2, 0.85, 0.25, 1);
      transform-style: preserve-3d;
      transform: rotateY(${flipped ? 180 : 0}deg);
    }

    .flip-card-face {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: ${largeSpacing}px;
      border-radius: ${CARD_RADIUS}px;
      backface-visibility: hidden;
      color: ${gray1};
      overflow-wrap: break-word;
      word-break: break-word;
      transition: box-shadow 0.3s ease, transform 0.3s ease;
    }

    .flip-card-front {
      background: ${white};
      border: 1px solid ${gray5};
      border-top: ${quarterSpacing}px solid ${warning};
      box-shadow: 0 4px 16px ${hexToRgbaString(black, 0.06)};
    }

    .flip-card-back {
      background: ${gray7};
      border: 1px solid ${primary};
      transform: rotateY(180deg);
    }

    &:hover .flip-card-face {
      box-shadow: 0 8px 24px ${hexToRgbaString(black, 0.12)};
    }

    @media (prefers-reduced-motion: reduce) {
      .flip-card-inner { transition: none; }
    }
  `;

  // eslint-disable-next-line no-nested-ternary
  const frontSizeClass = card.front.length >= 140 ? 'text-medium' : card.front.length >= 90 ? 'text-large-body' : 'text-xl';

  const visibleText = flipped ? card.back : card.front;
  const flipInstruction = flipped
    ? t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.A11Y.VIEW_FRONT()
    : t.LECTURE_PAGES.COMPONENTS.FLIP_CARD.A11Y.VIEW_BACK();

  return (
    <ClickableContainer
      onClick={() => setFlipped((prev) => !prev)}
      aria-label={`${visibleText}. ${flipInstruction}`}
      data-qa={config.pendo.flipCard.flip}
      data-qa-id={`${config.pendo.flipCard.flip}_${card.id}`}
    >
      <div css={styles}>
        <div className='flip-card-inner'>
          <div
            className={`flip-card-face flip-card-front ${frontSizeClass} font-weight-light`}
            aria-hidden={flipped}
          >
            {card.front}
          </div>
          <div
            className='flip-card-face flip-card-back text-medium font-weight-normal'
            aria-hidden={!flipped}
          >
            {card.back}
          </div>
        </div>
        <NvIcon className='flip-indicator' icon='recurring' size='xs-smallest' />
      </div>
    </ClickableContainer>
  );
};

export default FlipCard;
